import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGetShows, type ShowDTO } from '../api';
import { useProfileContext } from '../hooks/useProfileContext';
import PageLoadError from '../components/PageLoadError';
import { findShowToday } from '../lib/dayOf';

/**
 * P2e — the contextual BAND LANDING (`/band/:bandSlug/home`).
 *
 * Every "I picked a band" entry point (the /home band grid, the mono-band
 * auto-open, the band selector) navigates HERE instead of the old fixed
 * `/tour/shows`. This page then picks the right home for the moment:
 *   - a show of the band is dated TODAY (client local date) → Day-of page;
 *   - otherwise                          → Atlas page.
 * The decision reads the band's REAL shows through the existing read API —
 * nothing is invented, nothing is persisted, the backend is untouched.
 *
 * Never hidden: this is an ADDITION, not a replacement — Route map and Day of
 * show stay in the sidebar at all times (intelligence ≠ hiding a page), and
 * the `/home` landing (band grid) is unchanged.
 *
 * Fallbacks are the pre-P2e behaviour, never a guess:
 *   - the shows read fails           → the SHARED, readable error state with a
 *                                      Retry (lot 3a, 2026-10-14). It used to
 *                                      bounce SILENTLY to /tour/shows, which
 *                                      hides the failure and moves the user off
 *                                      the URL they asked for;
 *   - profiles still loading / none  → wait, then /tour/shows.
 * `replace` keeps the band grid out of the back stack (back = the grid).
 */
export default function BandLandingPage() {
  const { t } = useTranslation();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const { profiles, activeProfileId, loading: profilesLoading } = useProfileContext();

  // URL slug wins, the active profile is the fallback — same rule as
  // AtlasPage / DayOfPage (AppLayout's URL-sync keeps the profile in step).
  const band = useMemo(() => {
    const fromUrl = bandSlug ? profiles.find((p) => p.slug === bandSlug) ?? null : null;
    const fromCtx = activeProfileId ? profiles.find((p) => p.id === activeProfileId) ?? null : null;
    return fromUrl || fromCtx;
  }, [profiles, bandSlug, activeProfileId]);
  const bandId = band?.id || '';
  const base = band?.slug || bandId;

  type Landing = 'deciding' | 'dayof' | 'atlas' | 'shows';
  const [landing, setLanding] = useState<Landing>('deciding');
  // Lot 3a — a failed read used to be swallowed: the page bounced to
  // /tour/shows as if the router had simply "decided" something else. The user
  // lost the URL they opened AND any hint that a request had failed. Keep the
  // failure, show it, and offer a Retry that re-runs THIS read.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!bandId || !base) {
      // No band resolvable: only give up once the profile list is in — a
      // still-loading context would otherwise bounce a valid band to Shows.
      if (!profilesLoading) setLanding('shows');
      return;
    }
    let cancelled = false;
    setLanding('deciding');
    setLoadError(null);
    apiGetShows({ band: bandId })
      .then((shows: ShowDTO[]) => {
        if (cancelled) return;
        setLanding(findShowToday(Array.isArray(shows) ? shows : []) ? 'dayof' : 'atlas');
      })
      .catch((e: unknown) => {
        // Read failure: surface it instead of silently redirecting.
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [bandId, base, profilesLoading, retryToken]);

  if (loadError) {
    return (
      <div className="page">
        <PageLoadError
          title={band?.name || t('app.name')}
          detail={loadError}
          onRetry={() => setRetryToken((n) => n + 1)}
        />
      </div>
    );
  }
  if (landing === 'dayof') return <Navigate to={`/band/${base}/day-of`} replace />;
  if (landing === 'atlas') return <Navigate to={`/band/${base}/atlas`} replace />;
  if (landing === 'shows') return <Navigate to="/tour/shows" replace />;

  // Deciding (shows read in flight) — no invented string, the shell + sidebar
  // stay mounted; the destination page renders its own loading state right
  // after the redirect.
  return <div className="page" data-bandlanding="deciding" />;
}
